// src/pages/UserLogin.jsx

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  IconButton
} from '@mui/material';

import {
  ArrowBack as ArrowBackIcon,
  Login as LoginIcon
} from '@mui/icons-material';

export default function UserLogin() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ user_id: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleUserLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const res = await fetch("https://py.taih.ai/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form)
      });

      const data = await res.json();

      if (res.ok) {
        if (data.is_admin) {
          setError("Admin accounts cannot login through User Login.");
          setLoading(false);
          return;
        }

        // Save user data for dashboard
        sessionStorage.setItem("userData", JSON.stringify(data));

        navigate("/user/dashboard");
      } else {
        setError(data.detail || "Invalid login credentials.");
      }
    } catch (err) {
      setError("Network error. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa', fontFamily: 'Helvetica, Arial, sans-serif' }}>

      {/* HEADER */}
      <Box sx={{ py: 2, color: '#fff', bgcolor: '#004494' }}>
        <Container sx={{ textAlign: 'center', position: 'relative' }}>

          <IconButton
            onClick={() => navigate('/')}
            sx={{ 
              color: 'white', 
              position: 'absolute', 
              left: 0,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            <ArrowBackIcon />
          </IconButton>

          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 600, 
              color: 'white',
              fontFamily: 'Helvetica, Arial, sans-serif'
            }}
          >
            User Login
          </Typography>
        </Container>
      </Box>

      {/* MAIN */}
      <Container sx={{ py: 8 }}>
        <Paper 
          sx={{ 
            p: 6, 
            maxWidth: 420, 
            mx: 'auto',
            borderRadius: 3,
            boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.1)'
          }} 
          elevation={3}
        >
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <LoginIcon 
              sx={{ 
                fontSize: 48, 
                color: '#004494', 
                mb: 1 
              }} 
            />
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 300,
                fontFamily: 'Helvetica, Arial, sans-serif',
                fontSize: '32px',
                color: '#000'
              }}
            >
              Welcome Back
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'text.secondary',
                fontFamily: 'Helvetica, Arial, sans-serif',
                fontSize: '16px',
                fontWeight: 400
              }}
            >
              Login to view your assessment results
            </Typography>
          </Box>

          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3,
                fontFamily: 'Helvetica, Arial, sans-serif'
              }}
            >
              {error}
            </Alert>
          )}

          <form onSubmit={handleUserLogin}>
            <TextField
              fullWidth
              label="User ID"
              name="user_id"
              value={form.user_id}
              onChange={onChange}
              sx={{ 
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  fontFamily: 'Helvetica, Arial, sans-serif',
                  '&:hover fieldset': {
                    borderColor: '#004494',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#004494',
                  },
                },
                '& .MuiInputLabel-root': {
                  fontFamily: 'Helvetica, Arial, sans-serif',
                  '&.Mui-focused': {
                    color: '#004494',
                  }
                }
              }}
            />

            <TextField
              fullWidth
              label="Password"
              name="password"
              type="password"
              value={form.password}
              onChange={onChange}
              sx={{ 
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  fontFamily: 'Helvetica, Arial, sans-serif',
                  '&:hover fieldset': {
                    borderColor: '#004494',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#004494',
                  },
                },
                '& .MuiInputLabel-root': {
                  fontFamily: 'Helvetica, Arial, sans-serif',
                  '&.Mui-focused': {
                    color: '#004494',
                  }
                }
              }}
            />

            <Button
              fullWidth
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                backgroundColor: '#004494',
                color: 'white',
                fontFamily: 'Helvetica, Arial, sans-serif',
                fontSize: '16px',
                fontWeight: 400,
                borderRadius: '8px',
                boxShadow: '0px 4px 12px rgba(0, 68, 148, 0.3)',
                '&:hover': {
                  backgroundColor: '#003366',
                  color: 'white',
                  boxShadow: '0px 6px 16px rgba(0, 68, 148, 0.4)',
                },
                '&:disabled': {
                  backgroundColor: '#ccc',
                  color: 'rgba(0, 0, 0, 0.26)'
                }
              }}
            >
              {loading ? (
                <CircularProgress size={20} sx={{ color: 'inherit' }} />
              ) : (
                "Login"
              )}
            </Button>
          </form>
        </Paper>
      </Container>
    </Box>
  );
}